'use client'

import type { PersonalFinancingRates } from '@/types/cms'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

type PersistantStoreState = {
  // Holds either a selected card category string (e.g. 'platinum') or a
  // comparison-data object, depending on the flow that set it.
  creditCardData: string | object
  setCreditCardData: (value: string | object) => void
}

export const usePersistantStore = create<PersistantStoreState>()(
  persist(
    set => ({
      creditCardData: {},
      setCreditCardData: value => set(() => ({ creditCardData: value }))
    }),
    {
      name: 'session-storage',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
)

/**
 * A DOM element ref held in the store (set from a component's `useRef(null)` and
 * read for scroll/measure). `current` is readonly so a freshly-created
 * `useRef(null)` (`{ current: null }`) assigns without ref-variance friction.
 */
type StoredRef = { readonly current: HTMLElement | null }

type StoreState = {
  calculatorState: PersonalFinancingRates
  setCalculatorState: (calculatorState: PersonalFinancingRates) => void
  tab: number
  setTab: (tab: number) => void
  formRef: StoredRef
  setFormRef: (ref: StoredRef) => void
  faqRef: StoredRef
  setFaqRef: (ref: StoredRef) => void
  globalAlertRef: StoredRef
  setGlobalAlertRef: (alertRef: StoredRef) => void
  alertHeight: number
  setAlertHeight: (alertHeight: number) => void
  showTips: boolean
  setShowTips: (showTips: boolean) => void
  showBanner: boolean
  setBanner: (showBanner: boolean) => void
}

const useStore = create<StoreState>(set => ({
  calculatorState: {},
  setCalculatorState: calculatorState => set(() => ({ calculatorState })),
  tab: 0,
  setTab: tab => set(() => ({ tab })),
  formRef: { current: null },
  setFormRef: ref => set(() => ({ formRef: ref })),
  faqRef: { current: null },
  setFaqRef: ref => set(() => ({ faqRef: ref })),
  globalAlertRef: { current: null },
  setGlobalAlertRef: alertRef => set(() => ({ globalAlertRef: alertRef })),
  alertHeight: 0,
  setAlertHeight: alertHeight => set(() => ({ alertHeight })),
  showTips: false,
  setShowTips: showTips => set(() => ({ showTips })),
  showBanner: false,
  setBanner: showBanner => set(() => ({ showBanner }))
}))

export default useStore
